import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Users, Megaphone, Upload, Activity, FileText, BarChart3,
    Settings as SettingsIcon, LogOut, KeyRound, UserCheck, GitBranch,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/leads", label: "Leads", icon: Users, testid: "nav-leads" },
    { to: "/clients", label: "Clients", icon: UserCheck, testid: "nav-clients" },
    { to: "/campaigns", label: "Campaigns", icon: Megaphone, testid: "nav-campaigns" },
    { to: "/templates", label: "Templates", icon: FileText, testid: "nav-templates" },
    { to: "/imports", label: "Imports", icon: Upload, testid: "nav-imports" },
    { to: "/activity", label: "Activity", icon: Activity, testid: "nav-activity" },
    { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
    { to: "/admin/workflow", label: "Workflow", icon: GitBranch, testid: "nav-workflow", adminOnly: true },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Sidebar({ onNavigate }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };
    return (
        <aside className="h-full w-full flex flex-col bg-white border-r border-border">
            <div className="px-5 py-5 border-b border-border">
                <Logo />
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                {NAV.filter(item => !item.adminOnly || user?.role === "admin").map(item => {
                    const Icon = item.icon;
                    return (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            onClick={onNavigate}
                            data-testid={item.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-primary/10 text-primary"
                                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                                }`
                            }
                        >
                            <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                            <span>{item.label}</span>
                        </NavLink>
                    );
                })}
            </nav>
            <div className="border-t border-border px-3 py-3">
                <div className="flex items-center gap-3 px-2 py-2">
                    <div className="h-8 w-8 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
                        {user?.name?.[0] ?? "A"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate" data-testid="sidebar-user-name">
                            {user?.name}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                    </div>
                    <button
                        onClick={() => navigate("/change-password")}
                        className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                        data-testid="sidebar-change-password"
                        aria-label="Change password"
                        title="Change password"
                    >
                        <KeyRound className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                    <button
                        onClick={handleLogout}
                        className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                        data-testid="logout-button"
                        aria-label="Log out"
                    >
                        <LogOut className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                </div>
            </div>
        </aside>
    );
}
